import CustomStore from 'devextreme/data/custom_store'
import { AxiosResponse } from 'axios'
import { axiosInstance } from '@/http-common'

function isNotEmpty(value: unknown): boolean {
    return value !== undefined && value !== null && value !== ''
}

/** DevExtreme loadOptions (+ extra params) -> query string for the API list endpoints. */
function loadOptionsParams(loadOptions: unknown, extraParams: Record<string, string>): URLSearchParams {
    const params = new URLSearchParams();
    ['skip', 'take', 'sort', 'filter'].forEach((option) => {
        const value = (loadOptions as Record<string, unknown>)[option]
        if (isNotEmpty(value)) {
            params.set(option, JSON.stringify(value))
        }
    })
    Object.entries(extraParams).forEach(([key, value]) => {
        if (value) params.set(key, value)
    })

    return params
}

/**
 * Remote store for the tacho drivers grid: paging, sorting and column filtering run on
 * the database (`/api/v1/tacho/activity/drivers`, DevExtreme loadOptions). The response
 * also carries whole-company stats for the tiles above the grid — they are handed to the
 * component through `onLoaded` on every (re)load.
 */
export function createDriversStore(
    getExtraParams: () => Record<string, string>,
    onLoaded: (stats: Record<string, number>) => void,
    onError: (message: string) => void
): CustomStore {
    return new CustomStore({
        key: 'driver_id',
        load: (loadOptions) => {
            const params = loadOptionsParams(loadOptions, getExtraParams())

            return axiosInstance.get(`/api/v1/tacho/activity/drivers?${ params.toString() }`)
                .then((response: AxiosResponse) => {
                    onLoaded(response.data.stats ?? {})
                    return { data: response.data.data ?? [], totalCount: response.data.totalCount ?? 0 }
                })
                .catch((error) => {
                    onError(error?.response?.data?.error || '')
                    throw error
                })
        }
    })
}

/**
 * Remote store for one driver's "downloaded files" import-candidate grid
 * (`/api/v1/tacho/activity/driver/{id}/import-candidates`) — same server-side
 * paging/sorting/filtering contract as the drivers grid.
 */
export function createImportCandidatesStore(
    getDriverId: () => number,
    onLoaded: (totalCount: number) => void,
    onError: (message: string) => void
): CustomStore {
    return new CustomStore({
        key: 'job_id',
        load: (loadOptions) => {
            const params = loadOptionsParams(loadOptions, {})

            return axiosInstance.get(`/api/v1/tacho/activity/driver/${ getDriverId() }/import-candidates?${ params.toString() }`)
                .then((response: AxiosResponse) => {
                    const totalCount = response.data.totalCount ?? 0
                    onLoaded(totalCount)
                    return { data: response.data.data ?? [], totalCount }
                })
                .catch((error) => {
                    onError(error?.response?.data?.error || '')
                    throw error
                })
        }
    })
}
